// Types mirror the shape produced by server/build.js.
// Kept here (not in a shared package) since the codebase is small.

import { createContext, useContext } from "react";

export type PlayerLite = { id: string; name: string; initials: string; color: string };

export type RuleUpdate = { id: string; title: string; summary: string };

export type Viewer =
  | { role: "player"; player: PlayerLite; pending_rule_updates: RuleUpdate[] }
  | { role: "spectator"; email: string; name: string };

export type Pick = {
  player_id: string;
  home_score: number;
  away_score: number;
  predicted_winner: "home" | "away" | null;
  points: number | null;
};

export type MyPick = {
  match_id: string;
  home_score: number;
  away_score: number;
  predicted_winner: "home" | "away" | null;
  submitted_at: string;
};

export type Match = {
  id: string;
  round: "R32" | "R16" | "QF" | "SF" | "3P" | "F";
  home: string;
  away: string;
  kickoff_utc: string;
  lock_at_utc: string;
  pick_window_open_utc: string;
  match_day_pt: string;
  locked: boolean;
  status: "scheduled" | "live" | "final" | "final_pens";
  home_score: number | null;
  away_score: number | null;
  pens_winner: "home" | "away" | null;
  actual: { home: number; away: number; winner: "home" | "away" | null } | null;
  scoring: Scoring;
  max_points: number;
  picks?: Pick[];
};

export type Scoring = { winner: number; exact: number };

export type LeaderboardEntry = {
  player_id: string;
  name: string;
  color: string;
  initials: string;
  total: number;
  delta: number;
  points_gained: number;
  exact_count: number;
  goal_difference_count: number;
  missed_predictions: number;
  score_error: number;
  rank: number;
};

export type RecentPick = {
  match_id: string;
  round: string;
  home: string;
  away: string;
  kickoff_utc: string;
  pick: { home: number; away: number; winner: "home" | "away" | null } | null;
  actual: { home: number; away: number; winner: "home" | "away" | null } | null;
  points: number;
  missing_award: boolean;
  missing_limit_exhausted: boolean;
  max_points: number;
};

export type FormEntry = {
  points: number;
  max_points: number;
  missing_award: boolean;
};

export type Profile = {
  player_id: string;
  total: number;
  exact_count: number;
  goal_difference_count: number;
  missed_predictions: number;
  score_error: number;
  correct_calls: number;
  accuracy: number;
  form: FormEntry[];
  streak: number;
  recent_picks: RecentPick[];
};

export type FunFacts = {
  contrarian_badges: Array<{ match_id: string; player_id: string }>;
  only_you_hits: Record<string, number>;
  biggest_jump: { player_id: string; match_id: string; matchday: string; delta: number } | null;
  longest_streaks: Record<string, number>;
};

export type State = {
  generated_at: string;
  config: {
    tournament: string;
    scoring: Scoring;
    scoring_by_round?: Partial<Record<Match["round"], Scoring>>;
    lock_offset_hours: number;
    missing_pick_policy?: string;
    missing_pick_group_minimum_limit?: number;
    missing_pick_limit_from_round?: Match["round"];
    missing_pick_limit_from_pt_date?: string;
    rule_updates?: RuleUpdate[];
  };
  players: PlayerLite[];
  matches: Match[];
  leaderboard: LeaderboardEntry[];
  profiles: Record<string, Profile>;
  history: Array<{
    match_id: string;
    kickoff_utc: string;
    matchday: string;
    points_gained: Record<string, number>;
    totals: Record<string, number>;
    ranks: Record<string, number>;
    tiebreakers: {
      exact_counts: Record<string, number>;
      goal_difference_counts: Record<string, number>;
      missed_predictions: Record<string, number>;
      score_errors: Record<string, number>;
    };
  }>;
  fun_facts: FunFacts;
};

// ---------------------------------------------------------------------------
// Context — App provides State, current player, and a refresh fn.
// ---------------------------------------------------------------------------

export type AppCtx = {
  state: State;
  me: PlayerLite | null;
  viewerRole: Viewer["role"];
  myPicks: Record<string, MyPick>;
  setMe: (id: string | null) => void;
  refresh: () => Promise<void>;
};

export const AppContext = createContext<AppCtx | null>(null);

export function useApp(): AppCtx {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside <AppProvider>");
  return ctx;
}
